import 'flowbite';
import { Suspense, lazy, Show, onMount } from 'solid-js';
import type { Component } from 'solid-js';
import { Route, Navigate, Router } from '@solidjs/router';

import { ToastProvider, BreadCrumbsProvider, twclsx } from './shared';
import { Login, RegisterUser, Error, Recover, Verification, Settings } from './user';
import Loading from './shared/Loading';
import ComingSoon from './shared/ComingSoon';
import Misc from './configs/Misc';

const BalanceSheet = lazy(() => import('./reports/BalanceSheet'));
const ProfitLoss = lazy(() => import('./reports/ProfitLoss'));
const TrialBalance = lazy(() => import('./reports/TrialBalance'));
const GainSummary = lazy(() => import('./reports/GainSummary'));
const StockHolding = lazy(() => import('./reports/StockHolding'));
const AccountBooks = lazy(() => import('./reports/AccountBooks'));
const GST = lazy(() => import('./reports/GST'));
const AuthProvider = lazy(() => import('./shared/AuthProvider'));
const NavBar = lazy(() => import('./navbar'));
const Sidebar = lazy(() => import('./navbar/Sidebar'));
const Entities = lazy(() => import('./entity/Entities'));
const Entity = lazy(() => import('./entity/Entity'));
const Transaction = lazy(() => import('./transaction'));
const Product = lazy(() => import('./product'));
const Account = lazy(() => import('./account'));
const ProdcutClass = lazy(() => import('./product_class'));
const AccountCategory = lazy(() => import('./account_category'));
const MeasurementUnit = lazy(() => import('./measurement_units'));
const GSTConfigs = lazy(() => import('./configs/GST'));
const Invoice = lazy(() => import('./invoice'));
const Import = lazy(() => import('./import'));
const ImportBank = lazy(() => import('./import/bank'));
const PrecisionProvider = lazy(() => import('./shared/PrecisionProvider'));

// with Sidebar and BreadCrumbs
const wrapper = (withBC: boolean, Child: Component<{ class?: string }>) => () => (
  <>
    <PrecisionProvider>
      <Suspense fallback={<Loading />}>
        <NavBar class="print:hidden" />
      </Suspense>
      <Show when={!withBC}>
        <div id="main-content" class={twclsx('w-full', 'bg-gray-50', 'dark:bg-gray-900', 'print:ml-0')}>
          <main class={twclsx('w-full')}>
            <Suspense fallback={<Loading />}>
              <Child class={twclsx('justify-center', 'pt-11', 'h-full', 'print:pt-0')} />
            </Suspense>
          </main>
        </div>
      </Show>
      <Show when={withBC}>
        <div class={twclsx('flex', 'pt-14', 'bg-gray-50', 'dark:bg-gray-900', 'print:pt-0')}>
          <Suspense fallback={<Loading />}>
            <Sidebar class="print:hidden" />
          </Suspense>
          <main
            id="main-content"
            class={twclsx('w-full', 'bg-gray-50', 'lg:ml-64', 'dark:bg-gray-900', 'print:ml-0')}
          >
            <BreadCrumbsProvider class="print:hidden">
              <Suspense fallback={<Loading />}>
                <Child class={twclsx('justify-center', 'pt-11', 'print:pt-0')} />
              </Suspense>
            </BreadCrumbsProvider>
          </main>
        </div>
      </Show>
    </PrecisionProvider>
  </>
);

const wrapperWithBC = (Child: Component<{ class?: string }>) => wrapper(true, Child);
const wrapperWithoutBC = (Child: Component<{ class?: string }>) => wrapper(false, Child);

// TODO: create a wrapper component to show a banner to ask user to verify email
const App: Component = () => {
  onMount(async () => {
    if (['android', 'android_production'].includes(import.meta.env.MODE)) {
      const { App: CapacitorApp } = await import('@capacitor/app');

      const handleBackButton = () => {
        if (window.location.pathname === '/') {
          CapacitorApp.exitApp();
        } else {
          window.history.back();
        }
      };

      CapacitorApp.addListener('backButton', handleBackButton);
    }
  });

  return (
    <Router root={ToastProvider}>
      <Route>
        <Route path="/" component={() => <Navigate href="/entities" />} />;
        <Route path="/auth/register" component={RegisterUser} />
        <Route path="/auth/error" component={Error} />
        <Route path="/auth/login" component={Login} />
        <Route path="/auth/recover" component={Recover} />
        <Route path="/auth/verification" component={Verification} />
        <Route path={'/settings'} component={AuthProvider}>
          <Route path="" component={wrapperWithoutBC(Settings)} />
        </Route>
        <Route path={'/entities'} component={AuthProvider}>
          <Route path="" component={wrapperWithoutBC(Entities)} />
          <Route path="/:entityId">
            <Route path="" component={wrapperWithBC(Entity)} />
            <Route path="/transactions/:transactionType" component={wrapperWithBC(Transaction)} />
            <Route path="/account-categories" component={wrapperWithBC(AccountCategory)} />
            <Route path="/accounts/:accountId?" component={wrapperWithBC(Account)} />
            <Route path="/product-classes" component={wrapperWithBC(ProdcutClass)} />
            <Route path="/products" component={wrapperWithBC(Product)} />
            <Route path="/measurement-units" component={wrapperWithBC(MeasurementUnit)} />
            <Route path={'/reports'}>
              <Route path="/balance-sheet" component={wrapperWithBC(BalanceSheet)} />
              <Route path="/profit-loss" component={wrapperWithBC(ProfitLoss)} />
              <Route path="/trial-balance" component={wrapperWithBC(TrialBalance)} />
              <Route path="/gain-summary" component={wrapperWithBC(GainSummary)} />
              <Route path="/stock-holding" component={wrapperWithBC(StockHolding)} />

              <Route path={'/ab'}>
                <Route path="/:abType" component={wrapperWithBC(AccountBooks)} />
              </Route>
              <Route path={'/s'}>
                <Route path="/gstr1" component={wrapperWithBC(GST)} />
              </Route>
            </Route>
            <Route path={'/configs'}>
              <Route path="/gst" component={wrapperWithBC(GSTConfigs)} />
              <Route path="/misc" component={wrapperWithBC(Misc)} />
              <Route path="/income-tax" component={wrapperWithBC(ComingSoon)} />
              <Route path="/depreciation" component={wrapperWithBC(ComingSoon)} />
              <Route path="/interest" component={wrapperWithBC(ComingSoon)} />
            </Route>
            <Route path="/invoices" component={wrapperWithBC(Invoice)} />{' '}
            <Route path={'/imports'}>
              <Route path="" component={wrapperWithBC(Import)} />
              <Route path="/banks" component={wrapperWithBC(ImportBank)} />
            </Route>
          </Route>
        </Route>
      </Route>
    </Router>
  );
};

export default App;
